<template>
    <div class="row g-3">
        <!-- Left Column: General Info & Responsible Persons -->
        <div class="col-lg-6">
            <!-- General Info Section -->
            <div class="meta-section-box mb-3">
                <div class="meta-section-header">
                    <span class="meta-header-icon meta-header-icon--blue">
                        <i class="bi bi-info-circle-fill"></i>
                    </span>
                    <h6 class="mb-0 fw-bold meta-header-title">Ümumi məlumat</h6>
                </div>
                <div class="meta-content-body">
                    <div class="meta-row">
                        <span class="meta-label">Şəhadətnamə ID</span>
                        <span class="meta-value font-monospace fw-bold text-primary">{{ data.quotaId }}</span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Müqavilə tarixi</span>
                        <span class="meta-value">{{ data.quotaDateStr }}</span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Müqavilə nömrəsi</span>
                        <span class="meta-value font-monospace fw-bold">{{ data.quotaFullNumber }}</span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Baş Müqavilə tarixi</span>
                        <span class="meta-value">{{ data.generalDate }}</span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Baş Müqavilə Nömrəsi</span>
                        <span class="meta-value font-monospace">{{ data.generalFullNumber }}</span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Müddət</span>
                        <span class="meta-value">
                            <span class="meta-chip meta-chip--blue">{{ data.period }} ay</span>
                        </span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Valyuta</span>
                        <span class="meta-value">
                            <span class="meta-chip meta-chip--gray">{{ data.currency }}</span>
                        </span>
                    </div>
                    <div class="meta-row border-bottom-0">
                        <span class="meta-label">Ödənilmə qaydası</span>
                        <span class="meta-value">
                            <span class="meta-chip meta-chip--green">{{ data.payType }}</span>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Responsible Persons Section -->
            <div class="meta-section-box">
                <div class="meta-section-header">
                    <span class="meta-header-icon meta-header-icon--purple">
                        <i class="bi bi-people-fill"></i>
                    </span>
                    <h6 class="mb-0 fw-bold meta-header-title">Məsul şəxslər</h6>
                </div>
                <div class="meta-content-body">
                    <div class="meta-row">
                        <span class="meta-label">Agent</span>
                        <span class="meta-value d-flex align-items-center gap-2">
                            <span class="person-mini-avatar">{{ getInitials(data.agentName) }}</span>
                            <span>{{ data.agentName }}</span>
                        </span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Anderayter</span>
                        <span class="meta-value d-flex align-items-center gap-2">
                            <span class="person-mini-avatar avatar--blue">{{ getInitials(data.underwritingName) }}</span>
                            <span>{{ data.underwritingName }}</span>
                        </span>
                    </div>
                    <div class="meta-row border-bottom-0">
                        <span class="meta-label">Kurator</span>
                        <span class="meta-value d-flex align-items-center gap-2">
                            <span class="person-mini-avatar avatar--green">{{ getInitials(data.curatorName) }}</span>
                            <span>{{ data.curatorName }}</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Right Column: Insured Company, Beneficiary & Signatures -->
        <div class="col-lg-6">
            <!-- Insured Company Section -->
            <div class="meta-section-box mb-3">
                <div class="meta-section-header">
                    <span class="meta-header-icon meta-header-icon--green">
                        <i class="bi bi-building-fill"></i>
                    </span>
                    <h6 class="mb-0 fw-bold meta-header-title">Sığortalı (Şirkət)</h6>
                </div>
                <div class="meta-content-body">
                    <div class="meta-row">
                        <span class="meta-label">Şirkətin tam adı</span>
                        <span class="meta-value fw-bold text-dark-emphasis">{{ data.insured?.name }}</span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">VÖEN</span>
                        <span class="meta-value">
                            <span class="font-monospace fw-bold text-info">{{ data.insured?.voen }}</span>
                        </span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Biznes sektoru</span>
                        <span class="meta-value">
                            <span class="meta-chip meta-chip--amber">{{ data.insured?.clientSector }}</span>
                        </span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Telefon</span>
                        <span class="meta-value">
                            <a :href="'tel:' + data.insured?.phone" class="phone-meta-link">
                                <i class="bi bi-telephone me-1"></i>{{ data.insured?.phone }}
                            </a>
                        </span>
                    </div>
                    <div class="meta-row border-bottom-0">
                        <span class="meta-label">Hüquqi ünvan</span>
                        <span class="meta-value text-muted" style="line-height: 1.35;">{{ data.insured?.address }}</span>
                    </div>
                </div>
            </div>

            <!-- Beneficiary Person Section -->
            <div class="meta-section-box mb-3">
                <div class="meta-section-header">
                    <span class="meta-header-icon meta-header-icon--amber">
                        <i class="bi bi-person-check-fill"></i>
                    </span>
                    <h6 class="mb-0 fw-bold meta-header-title">Ödəyici / Faydalanan</h6>
                </div>
                <div class="meta-content-body">
                    <div class="meta-row">
                        <span class="meta-label">Tam adı</span>
                        <span class="meta-value fw-bold d-flex align-items-center gap-2">
                            <span class="person-mini-avatar avatar--amber">{{ getInitials(data.beneficiary?.name) }}</span>
                            <span>{{ data.beneficiary?.name }}</span>
                        </span>
                    </div>
                    <div class="meta-row border-bottom-0">
                        <span class="meta-label">Yaşayış ünvanı</span>
                        <span class="meta-value text-muted" style="line-height: 1.35;">{{ data.beneficiary?.address }}</span>
                    </div>
                </div>
            </div>

            <!-- Signatures Section -->
            <div class="meta-section-box">
                <div class="meta-section-header">
                    <span class="meta-header-icon meta-header-icon--blue">
                        <i class="bi bi-pen-fill"></i>
                    </span>
                    <h6 class="mb-0 fw-bold meta-header-title">İmzalar və Təsdiq</h6>
                </div>
                <div class="meta-content-body">
                    <div class="meta-row">
                        <span class="meta-label">Sığortaçı</span>
                        <span class="meta-value d-flex align-items-center gap-1">
                            <i class="bi bi-check-circle-fill text-success me-1"></i>
                            <span class="font-monospace text-success fw-bold">{{ data.insurerSignDate }}</span>
                        </span>
                    </div>
                    <div class="meta-row">
                        <span class="meta-label">Kurator</span>
                        <span class="meta-value d-flex align-items-center gap-1">
                            <template v-if="data.curatorSignDate">
                                <i class="bi bi-check-circle-fill text-success me-1"></i>
                                <span class="font-monospace text-success fw-bold">{{ data.curatorSignDate }}</span>
                            </template>
                            <template v-else>
                                <span class="badge bg-warning-subtle text-warning fw-bold">Gözləmədə</span>
                            </template>
                        </span>
                    </div>
                    <div class="meta-row border-bottom-0">
                        <span class="meta-label">Müştəri</span>
                        <span class="meta-value d-flex align-items-center gap-1">
                            <i class="bi bi-check-circle-fill text-success me-1"></i>
                            <span class="font-monospace text-success fw-bold">{{ data.clientSignDate }}</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "PolicyInfoData",
    props: {
        data: {
            type: Object,
            default: () => ({})
        }
    },
    methods: {
        getInitials(name) {
            if (!name) return "TP";
            const parts = name.trim().split(/\s+/);
            if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
    }
};
</script>

<style scoped lang="scss">
.meta-section-box {
    background: var(--app-surface-soft, #f8fafc);
    border: 1px solid var(--app-border, #e2e8f0);
    border-radius: var(--app-radius, 8px);
    overflow: hidden;
    transition: all 0.15s ease;
}

.meta-section-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: var(--app-surface, #ffffff);
    border-bottom: 1px solid var(--app-border, #e2e8f0);
}

.meta-header-icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.88rem;

    &--blue {
        background: rgba(11, 79, 156, 0.12);
        color: var(--bs-info, #0b4f9c);
    }
    &--purple {
        background: rgba(147, 51, 234, 0.12);
        color: #9333ea;
    }
    &--green {
        background: rgba(16, 185, 129, 0.12);
        color: #059669;
    }
    &--amber {
        background: rgba(245, 158, 11, 0.12);
        color: #d97706;
    }
}

.meta-header-title {
    font-size: 0.88rem;
    color: var(--app-heading, #1e293b);
}

.meta-content-body {
    padding: 4px 14px;
}

.meta-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid var(--app-border, #e2e8f0);
    font-size: 0.82rem;
}

.meta-label {
    color: var(--app-text, #64748b);
    font-weight: 550;
    min-width: 140px;
    flex-shrink: 0;
}

.meta-value {
    color: var(--app-heading, #1e293b);
    text-align: right;
    word-break: break-word;
}

.meta-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 9999px;
    font-size: 0.74rem;
    font-weight: 700;

    &--blue {
        background: rgba(11, 79, 156, 0.1);
        color: var(--bs-info, #0b4f9c);
    }
    &--green {
        background: rgba(16, 185, 129, 0.12);
        color: #059669;
    }
    &--amber {
        background: rgba(245, 158, 11, 0.14);
        color: #b45309;
    }
    &--gray {
        background: rgba(100, 116, 139, 0.12);
        color: #475569;
    }
}

.person-mini-avatar {
    width: 24px;
    height: 24px;
    border-radius: 5px;
    background: rgba(147, 51, 234, 0.14);
    color: #7c3aed;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    font-weight: 800;

    &.avatar--blue {
        background: rgba(37, 99, 235, 0.14);
        color: #2563eb;
    }
    &.avatar--green {
        background: rgba(16, 185, 129, 0.14);
        color: #059669;
    }
    &.avatar--amber {
        background: rgba(245, 158, 11, 0.14);
        color: #d97706;
    }
}

.phone-meta-link {
    color: var(--bs-info, #0b4f9c);
    text-decoration: none;
    font-weight: 600;

    &:hover {
        text-decoration: underline;
    }
}

[data-theme="dark"] {
    .meta-section-box {
        background: #141b22;
        border-color: #283544;
    }

    .meta-section-header {
        background: #182029;
        border-color: #283544;
    }

    .meta-header-title {
        color: #f1f5f9;
    }

    .meta-row {
        border-color: #242f3d;
    }

    .meta-label {
        color: #94a3b8;
    }

    .meta-value {
        color: #f1f5f9;
    }

    .meta-chip--gray {
        background: rgba(255, 255, 255, 0.1);
        color: #cbd5e1;
    }

    .phone-meta-link {
        color: #60a5fa;
    }
}
</style>